import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  goodsReturnsFromCustomer,
  type GRFCListItem,
} from '@/api/goodsReturns';
import { contractors as contractorsApi } from '@/api/catalogs';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import type { ColDef } from 'ag-grid-community';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import DocListFilters, { ContractorFilter, DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { agOrgCol, useOrgNames } from '@/shared/hooks/useOrgNames';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function GoodsReturnsFromCustomerPage() {
  const orgNames = useOrgNames();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Стандартні фільтри списків (еталон /employees).
  const f = useDocListFilters();
  // 2026-07-31 (рішення власника): пошуковий фільтр за покупцем.
  const [contractorFilter, setContractorFilter] = useState('');
  // 2026-08-09 (Норма #29): серверна пагінація + сортування.
  const { datasource, total } = useInfiniteGridDatasource<GRFCListItem>(
    (limit, offset, sort) => goodsReturnsFromCustomer.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      contractor_id: contractorFilter || undefined,
      date_from: f.dateFrom, date_to: f.dateTo,
    }),
    [contractorFilter, ...f.queryKey],
    { attachmentsType: 'doc.goods_returns_from_customer' },
  );
  // Контроль підписання (0429): колонка «Підпис» + фон непідписаних.
  const sc = useSignControlInfinite<GRFCListItem>('doc.goods_returns_from_customer', f.orgId);
  // UX α: orgList більше не використовується — переключилися на
  // availableOrgs з usePermissionsByOrg (показуємо лише org з
  // sales.return.manage permission).
  const {
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
  } = usePermissionsByOrg('sales.return.manage');
  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const cName = useMemo(() => {
    const m = new Map<string, string>();
    cQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cQ.data]);

  const cols = useMemo<ColDef<GRFCListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: GRFCListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№', width: 150, sortable: true },
    {
      field: 'document_date', headerName: t('cd.date'), width: 170, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString(i18n.language) : '',
    },
    agOrgCol(orgNames),
    {
      field: 'contractor_id', headerName: 'Покупець', flex: 1, minWidth: 220, sortable: false,
      valueFormatter: (p) => cName.get(p.value) ?? '',
    },
    {
      field: 'total_amount', headerName: t('cd.amount'), width: 140, sortable: true,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt(p.value) : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) =>
        p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: t('cd.posted'), width: 130, sortable: true,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [t, i18n.language, cName]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Повернення від покупців<TitleTotal total={total} /></h2>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/goods-returns-from-customer/new')}
          disabled={!hasPermissionInAnyOrg}
          title={!hasPermissionInAnyOrg
            ? `У вас немає прав створювати повернення у жодній з ${totalAccessibleOrgs} ваших організацій`
            : undefined
          }
        >
          + Нове повернення
        </button>
      </div>
      <DocListFiltersRow>
        {/* 2026-08-29 (рішення власника): фільтр статусу прибрано — статус видно колонкою. */}
        <DocListFilters f={f} />
        <ContractorFilter value={contractorFilter} onChange={setContractorFilter} />
      </DocListFiltersRow>
      <InfiniteDocGrid<GRFCListItem>
        copyRow={(r) => navigate(`/goods-returns-from-customer/new?copy=${r.id}`)}
        deleteRow={{ remove: (id) => goodsReturnsFromCustomer.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="grfc"
        onRowDoubleClicked={(r) => navigate(`/goods-returns-from-customer/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />

    </div>
  );
}
